/**
 * AttachmentsCard — reusable file list + uploader for any owner entity.
 *
 * Backed by the shared `attachments.*` router (generic over
 * `ownerType` + `ownerId`), so invoices and any future owner share one card.
 * Two-phase, direct-to-storage upload (mirrors the bill flow):
 *   1. `attachments.requestUpload` → presigned PUT URL + key
 *   2. `fetch(url, { method: 'PUT', body: file })` straight to storage (MinIO)
 *   3. `attachments.confirm` with the same key + metadata → row + audit
 *
 * Type/size are validated client-side before `requestUpload` (the server
 * re-validates on both calls). Clicking a row opens its public URL.
 *
 * @see apps/api/src/routers/attachments.ts
 * @see packages/domain/src/attachments/
 */
import { useId, useRef, useState } from "react";
import { FileImage, FileText, Loader2, Plus, Trash2, X } from "lucide-react";
import { Alert, Button } from "@flint/ui";
import { trpc } from "../../lib/trpc.js";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const ACCEPT_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 25 * 1024 * 1024; // matches ATTACHMENT_MAX_BYTES (domain re-validates anyway)

type AcceptType = "application/pdf" | "image/jpeg" | "image/png" | "image/webp";
type OwnerType = "invoice" | "garment_sample_submission";

interface AttachmentsCardProps {
  ownerType: OwnerType;
  ownerId: string;
  /** Defaults: "No attachments yet." / "Add file". */
  emptyLabel?: string | undefined;
  addLabel?: string | undefined;
}

interface UploadProgress {
  id: string;
  file: File;
  state: "requesting" | "uploading" | "confirming" | "error";
  message?: string;
}

export function AttachmentsCard({ ownerType, ownerId, emptyLabel, addLabel }: AttachmentsCardProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const list = trpc.attachments.list.useQuery({ ownerType, ownerId });
  const requestUpload = trpc.attachments.requestUpload.useMutation();
  // Attachments write an audit row too — refresh every panel that reads it, not just
  // the file list. The `reports.invoiceActivityFeed` branch keeps this generic
  // component owner-aware without leaking a callback to consumers.
  const invalidate = () =>
    Promise.all([
      utils.attachments.list.invalidate({ ownerType, ownerId }),
      ownerType === "invoice"
        ? utils.reports.invoiceActivityFeed.invalidate({ invoiceId: ownerId })
        : Promise.resolve(),
      utils.auditLog.list.invalidate(),
    ]);
  const confirm = trpc.attachments.confirm.useMutation({ onSuccess: invalidate });
  const remove = trpc.attachments.remove.useMutation({ onSuccess: invalidate });

  const [inflight, setInflight] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onFilesPicked = (files: FileList) => {
    setError(null);
    for (const file of Array.from(files)) {
      const invalid = validateFile(file);
      if (invalid) setError(invalid);
      else void uploadOne(file);
    }
  };

  /** request → PUT → confirm for a single file. */
  const uploadOne = async (file: File) => {
    const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const patch = (s: UploadProgress["state"], message?: string) =>
      setInflight((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, state: s, ...(message !== undefined ? { message } : {}) } : p,
        ),
      );
    setInflight((prev) => [...prev, { id, file, state: "requesting" }]);

    try {
      const req = await requestUpload.mutateAsync({
        ownerType,
        ownerId,
        originalName: file.name,
        contentType: file.type as AcceptType,
        sizeBytes: file.size,
      });
      patch("uploading");
      const put = await fetch(req.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed: ${put.status} ${put.statusText || ""}`.trim());
      patch("confirming");
      await confirm.mutateAsync({
        ownerType,
        ownerId,
        objectKey: req.objectKey,
        originalName: file.name,
        contentType: file.type as AcceptType,
        sizeBytes: file.size,
      });
      setInflight((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      patch("error", err instanceof Error ? err.message : String(err));
    }
  };

  const items = list.data?.items ?? [];

  return (
    <div className="grid min-w-0 gap-2">
      {error ? <Alert intent="bad" title="Couldn't add that file" description={error} /> : null}
      {list.error ? (
        <Alert intent="bad" title="Couldn't load attachments" description={list.error.message} />
      ) : null}

      {items.length === 0 && inflight.length === 0 && !list.isLoading ? (
        <p className="rounded-md border border-dashed border-line-2 bg-paper p-4 text-center text-sm text-muted">
          {emptyLabel ?? "No attachments yet."}
        </p>
      ) : null}

      <div className="grid max-h-[13.5rem] gap-2 overflow-y-auto pr-1">
        {items.map((item) => (
          <AttachmentRow
            key={item.id}
            contentType={item.contentType}
            name={item.originalName}
            meta={`${formatBytes(item.sizeBytes)} · ${formatRelative(item.uploadedAt)}`}
            publicUrl={item.publicUrl}
            onRemove={() => remove.mutate({ id: item.id })}
            removing={remove.isPending && remove.variables?.id === item.id}
          />
        ))}
        {inflight.map((p) => (
          <PendingRow key={p.id} progress={p} />
        ))}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        accept={ACCEPT}
        aria-label="Attach a file"
        className="sr-only"
        onChange={(e) => {
          if (e.target.files) onFilesPicked(e.target.files);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        intent="ghost"
        size="sm"
        className="mt-1 w-full min-w-0 border border-dashed border-line-2 text-sky-700"
        onClick={() => inputRef.current?.click()}
      >
        <Plus className="h-4 w-4" /> {addLabel ?? "Add file"}
      </Button>
      <p className="min-w-0 truncate text-xs text-muted" title="PDF, JPEG, PNG, or WebP. Max 25 MB.">
        PDF, JPEG, PNG, or WebP. Max 25 MB per file — pick several at once.
      </p>
    </div>
  );
}

function AttachmentRow({
  contentType,
  name,
  meta,
  publicUrl,
  onRemove,
  removing,
}: {
  contentType: string;
  name: string;
  meta: string;
  publicUrl: string | null;
  onRemove: () => void;
  removing: boolean;
}) {
  const isImage = contentType.startsWith("image/");
  const Icon = isImage ? FileImage : FileText;
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-md border border-line bg-raised p-3">
      {isImage && publicUrl ? (
        <a href={publicUrl} target="_blank" rel="noreferrer" className="block h-9 w-9 shrink-0 overflow-hidden rounded-sm border border-line">
          <img src={publicUrl} alt="" className="h-full w-full object-cover" />
        </a>
      ) : (
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-sm ${isImage ? "bg-sky-50 text-sky-700" : "bg-bad-50 text-bad"}`}
        >
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        {publicUrl ? (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="truncate text-sm font-semibold text-ink hover:text-sky-700"
            title={name}
          >
            {name}
          </a>
        ) : (
          <p className="truncate text-sm font-semibold text-ink" title={name}>
            {name}
          </p>
        )}
        <p className="truncate text-xs text-muted" title={meta}>
          {meta}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-sm text-muted hover:bg-bad-50 hover:text-bad disabled:opacity-40"
        aria-label={`Remove ${name}`}
      >
        {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

function PendingRow({ progress }: { progress: UploadProgress }) {
  const isError = progress.state === "error";
  const label: Record<UploadProgress["state"], string> = {
    requesting: "Requesting…",
    uploading: "Uploading…",
    confirming: "Confirming…",
    error: progress.message ?? "Upload failed",
  };
  return (
    <div
      className={`flex min-w-0 items-center gap-3 rounded-md border p-3 ${isError ? "border-bad-100 bg-bad-50" : "border-line bg-raised"}`}
    >
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-sm ${isError ? "bg-bad-50 text-bad" : "bg-sky-50 text-sky-700"}`}
      >
        {isError ? <X className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink" title={progress.file.name}>
          {progress.file.name}
        </p>
        <p className={`truncate text-xs ${isError ? "text-bad" : "text-muted"}`}>
          {label[progress.state]}
        </p>
      </div>
    </div>
  );
}

function validateFile(file: File): string | null {
  if (!ACCEPT_TYPES.has(file.type)) {
    return `"${file.name}" has unsupported type "${file.type || "unknown"}". Use PDF, JPEG, PNG, or WebP.`;
  }
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > MAX_BYTES) return `"${file.name}" exceeds the 5 MB limit.`;
  return null;
}

function formatBytes(bytes: string): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const seconds = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
